import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/shared/lib/utils";
import { Link, useLocation } from "@tanstack/react-router";
import type { LucideIcon } from "lucide-react";
import { useSidebarCollapsed } from "./sidebar-store";

interface SidebarNavLinkProps {
  to: string;
  label: string;
  icon: LucideIcon;
  badge?: number | string | undefined;
  testId?: string | undefined;
  onNavigate?: (() => void) | undefined;
  /** Sbalený sidebar — jen ikona, popis v nativním tooltipu (title). */
  collapsed?: boolean | undefined;
}

/** Jednotlivý odkaz v sidebaru. Aktivní stav: primary pozadí + bílý text + levý 2px akcent. */
export function SidebarNavLink({
  to,
  label,
  icon: Icon,
  badge,
  testId,
  onNavigate,
  collapsed: collapsedProp,
}: SidebarNavLinkProps) {
  const ctxCollapsed = useSidebarCollapsed();
  const collapsed = collapsedProp ?? ctxCollapsed;
  const location = useLocation();
  const isActive =
    to === "/dashboard" ? location.pathname === "/dashboard" : location.pathname.startsWith(to);

  const link = (
    <Link
      to={to}
      onClick={onNavigate}
      className={cn(
        "group relative flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
        collapsed && "justify-center px-2",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--sidebar-active-bg))] focus-visible:ring-offset-2 focus-visible:ring-offset-[hsl(var(--sidebar-bg))]",
        isActive
          ? "bg-[hsl(var(--sidebar-active-bg))] text-[hsl(var(--sidebar-active-fg))]"
          : "text-[hsl(var(--sidebar-fg-muted))] hover:bg-[hsl(var(--sidebar-bg-elevated))] hover:text-[hsl(var(--sidebar-fg))]",
      )}
      data-testid={testId}
      aria-current={isActive ? "page" : undefined}
      aria-label={collapsed ? label : undefined}
    >
      {isActive ? (
        <span
          aria-hidden="true"
          className="absolute left-0 top-1/2 h-6 w-[2px] -translate-y-1/2 rounded-r-full bg-[hsl(var(--sidebar-active-fg))]"
        />
      ) : null}
      <Icon className="h-4 w-4 shrink-0" strokeWidth={2} />
      {collapsed ? null : <span className="flex-1 truncate">{label}</span>}
      {!collapsed && badge !== undefined && badge !== 0 ? (
        <span
          className={cn(
            "ml-auto inline-flex min-w-[20px] items-center justify-center rounded-full px-1.5 py-0.5 text-xs font-semibold",
            isActive
              ? "bg-white/20 text-white"
              : "bg-[hsl(var(--sidebar-bg-elevated))] text-[hsl(var(--sidebar-fg))]",
          )}
          data-testid={testId ? `${testId}-badge` : undefined}
        >
          {badge}
        </span>
      ) : null}
    </Link>
  );

  if (!collapsed) return link;
  // Sbaleno: popis přes Radix Tooltip — funguje na hover i klávesnicový fokus
  // (title atribut je jen pro myš a se zpožděním).
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>{link}</TooltipTrigger>
        <TooltipContent side="right">{label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
